/**
 * Three-step setup for an operator-managed CSAF advisory source.
 *
 * Remote sources have to pass the write-free `/api/csaf/sources/test` probe for
 * their current kind/URL pair before they can be created. The probe result is
 * deliberately fingerprinted: going back and changing either value makes an
 * earlier green result stale. Manual sources are upload slots and therefore do
 * not pretend to have an external connection to test.
 */

import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { Banner } from '../../components/Banner';
import { Chip } from '../../components/Chip';
import EmptyState from '../../components/EmptyState';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Modal';
import Spinner from '../../components/Spinner';
import { createCsafSource, errorMessage, testCsafSource } from '../../lib/api';
import { fmtNumber } from '../../lib/format';
import type {
  CsafSource,
  CsafSourceKind,
  CsafSourcePreset,
  CsafSourceTestResponse,
} from '../../lib/types';

import './csaf-wizard.css';

type WizardStep = 1 | 2 | 3;

interface KindOption {
  kind: CsafSourceKind;
  label: string;
}

export interface CsafSourceWizardProps {
  /** A curated source opens the same wizard, only prefilled. */
  preset: CsafSourcePreset | null;
  kinds: readonly KindOption[];
  onClose: () => void;
  onCreated: (source: CsafSource) => void;
}

const STEP_LABELS: readonly { step: WizardStep; label: MessageDescriptor }[] = [
  { step: 1, label: msg`Quelle & URL` },
  { step: 2, label: msg`Verbindungstest` },
  { step: 3, label: msg`Abschließen` },
];

const KIND_HINTS: Readonly<Record<CsafSourceKind, MessageDescriptor>> = {
  rolie:
    msg`Die ROLIE-Feed-URL des Herstellers, z. B. https://cert-portal.siemens.com/productcert/csaf/ssa-feed-tlp-white.json`,
  feed:
    msg`Die RSS-/Atom-URL der Sicherheitsmeldungen. Der Test prüft den Feed und ein gefundenes CSAF-Dokument.`,
  document: msg`Die URL eines einzelnen CSAF-JSON-Dokuments. Es wird bei jedem Lauf neu geladen.`,
  manual: msg`Keine URL: CSAF-Dokumente werden später einzeln als JSON-Datei hochgeladen.`,
  euvd:
    msg`Leer lassen, um die offizielle ENISA-EUVD-API zu verwenden, oder eine abweichende API-Basis angeben.`,
};

function normaliseUrl(kind: CsafSourceKind, value: string): string {
  return kind === 'manual' ? '' : value.trim();
}

function urlIsValid(kind: CsafSourceKind, value: string): boolean {
  const candidate = normaliseUrl(kind, value);
  if (kind === 'manual') return candidate === '';
  if (kind === 'euvd' && candidate === '') return true;
  if (candidate === '' || candidate.length > 2048) return false;
  try {
    const parsed = new URL(candidate);
    return (
      (parsed.protocol === 'http:' || parsed.protocol === 'https:') &&
      parsed.hostname !== '' &&
      parsed.username === '' &&
      parsed.password === ''
    );
  } catch {
    return false;
  }
}

function fingerprint(kind: CsafSourceKind, url: string): string {
  return JSON.stringify([kind, normaliseUrl(kind, url)]);
}

export function CsafSourceWizard({
  preset,
  kinds,
  onClose,
  onCreated,
}: CsafSourceWizardProps): ReactNode {
  const { t, i18n } = useLingui();
  const [step, setStep] = useState<WizardStep>(1);
  const [name, setName] = useState(preset?.name ?? '');
  const [kind, setKind] = useState<CsafSourceKind>(preset?.kind ?? 'rolie');
  const [url, setUrl] = useState(preset?.url ?? '');
  const [testing, setTesting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<CsafSourceTestResponse | null>(null);
  const [testedFingerprint, setTestedFingerprint] = useState<string | null>(null);
  const actionInFlight = useRef(false);
  const stepHeading = useRef<HTMLHeadingElement | null>(null);
  const previousStep = useRef<WizardStep>(step);

  const cleanName = name.trim();
  const cleanUrl = normaliseUrl(kind, url);
  const currentFingerprint = fingerprint(kind, url);
  const nameValid = cleanName.length > 0 && cleanName.length <= 120;
  const urlValid = urlIsValid(kind, url);
  const sourceDetailsValid = nameValid && urlValid;
  const currentTestSucceeded =
    kind === 'manual' ||
    Boolean(
      testedFingerprint === currentFingerprint &&
        testResult?.kind === kind &&
        testResult.reachable &&
        testResult.compatible,
    );
  const busy = testing || creating;
  const stepLabel = STEP_LABELS[step - 1] ? i18n._(STEP_LABELS[step - 1]!.label) : '';
  const kindHint = i18n._(KIND_HINTS[kind]);

  // Moving between steps replaces the panel content. Put keyboard/screen-reader
  // focus on the new heading instead of leaving it on a footer button that no
  // longer exists.
  useEffect(() => {
    if (previousStep.current !== step) stepHeading.current?.focus();
    previousStep.current = step;
  }, [step]);

  function invalidateTest(): void {
    setTestResult(null);
    setTestedFingerprint(null);
    setTestError(null);
    setCreateError(null);
  }

  function changeKind(next: CsafSourceKind): void {
    setKind(next);
    if (next === 'manual') setUrl('');
    invalidateTest();
  }

  function changeUrl(next: string): void {
    setUrl(next);
    invalidateTest();
  }

  async function runTest(): Promise<void> {
    if (kind === 'manual' || !urlValid || actionInFlight.current) return;
    actionInFlight.current = true;
    setTesting(true);
    setTestError(null);
    setTestResult(null);
    const tested = currentFingerprint;
    try {
      const result = await testCsafSource({ kind, url: cleanUrl });
      setTestResult(result);
      setTestedFingerprint(tested);
    } catch (err) {
      setTestError(errorMessage(err));
      setTestedFingerprint(null);
    } finally {
      actionInFlight.current = false;
      setTesting(false);
    }
  }

  async function create(): Promise<void> {
    if (!sourceDetailsValid || !currentTestSucceeded || actionInFlight.current) return;
    actionInFlight.current = true;
    setCreating(true);
    setCreateError(null);
    try {
      const response = await createCsafSource({
        name: cleanName,
        kind,
        url: cleanUrl,
        import_all: true,
        record_style: 'cve',
      });
      onCreated(response.source);
    } catch (err) {
      setCreateError(errorMessage(err));
    } finally {
      actionInFlight.current = false;
      setCreating(false);
    }
  }

  const sampleCves = fmtNumber(testResult?.sample?.cves);
  const sampleId = testResult?.sample?.id ?? '';
  const sampleVersion = testResult?.sample?.csaf_version ? ` · CSAF ${testResult.sample.csaf_version}` : '';

  const footer = (
    <>
      <button type="button" className="ghost" disabled={busy} onClick={onClose}>
        <Trans>Abbrechen</Trans>
      </button>
      <span className="spacer" />
      {step > 1 ? (
        <button
          type="button"
          className="ghost"
          disabled={busy}
          onClick={() => setStep(step === 3 ? 2 : 1)}
        >
          <Trans>Zurück</Trans>
        </button>
      ) : null}
      {step === 1 ? (
        <button
          type="button"
          className="primary"
          disabled={!sourceDetailsValid}
          onClick={() => setStep(2)}
        >
          <Trans>Weiter zum Verbindungstest</Trans>
        </button>
      ) : step === 2 ? (
        <>
          {kind !== 'manual' ? (
            <button
              type="button"
              className={currentTestSucceeded ? 'ghost' : 'primary'}
              disabled={testing}
              onClick={() => void runTest()}
            >
              {testing ? (
                <Spinner variant="on-accent" label={t`Verbindung wird geprüft…`} />
              ) : testResult ? (
                <Trans>Erneut testen</Trans>
              ) : (
                <Trans>Verbindung testen</Trans>
              )}
            </button>
          ) : null}
          <button
            type="button"
            className="primary"
            disabled={!currentTestSucceeded || testing}
            onClick={() => setStep(3)}
          >
            <Trans>Weiter zum Abschluss</Trans>
          </button>
        </>
      ) : (
        <button
          type="button"
          className="primary"
          disabled={!currentTestSucceeded || creating}
          onClick={() => void create()}
        >
          {creating ? <Spinner variant="on-accent" label={t`Quelle wird angelegt…`} /> : t`Quelle anlegen`}
        </button>
      )}
    </>
  );

  return (
    <Modal
      title={t`CSAF-Quelle einrichten`}
      subtitle={t`Schritt ${step} von 3 · ${stepLabel}`}
      size="wide"
      dismissable={!busy}
      onClose={onClose}
      footer={footer}
    >
      <div className="csaf-wizard">
        <ol className="csaf-wizard-steps" aria-label={t`Einrichtungsfortschritt`}>
          {STEP_LABELS.map((entry) => (
            <li
              key={entry.step}
              className={entry.step < step ? 'done' : entry.step === step ? 'current' : undefined}
              aria-current={entry.step === step ? 'step' : undefined}
            >
              <span aria-hidden="true">{entry.step < step ? <Icon name="check" size={14} /> : entry.step}</span>
              {i18n._(entry.label)}
            </li>
          ))}
        </ol>

        {step === 1 ? (
          <section className="csaf-wizard-panel" aria-labelledby="csaf-wizard-source-title">
            <div className="csaf-wizard-panel-head">
              <div>
                <h3 id="csaf-wizard-source-title" ref={stepHeading} tabIndex={-1}>
                  <Trans>Quelle beschreiben</Trans>
                </h3>
              </div>
              {preset ? <Chip tone="info"><Trans>Offizielle Vorlage</Trans></Chip> : null}
            </div>

            {preset ? (
              <Banner
                sub={
                  <a href={preset.official_url} target="_blank" rel="noreferrer noopener">
                    <Trans>Offizielle Herstellerseite öffnen</Trans>
                  </a>
                }
              >
                {preset.description}
              </Banner>
            ) : null}

            <div className="csaf-wizard-fields">
              <div className="field">
                <label htmlFor="csaf-wizard-name"><Trans>Name</Trans></label>
                <input
                  id="csaf-wizard-name"
                  type="text"
                  value={name}
                  maxLength={120}
                  placeholder={t`z. B. Phoenix Contact PSIRT`}
                  aria-invalid={!nameValid}
                  aria-describedby="csaf-wizard-name-hint"
                  onChange={(event) => {
                    setName(event.target.value);
                    setCreateError(null);
                  }}
                />
                <span id="csaf-wizard-name-hint" className={!nameValid ? 'err' : 'hint'}>
                  {cleanName === '' ? t`Bitte einen eindeutigen Namen angeben.` : t`Dieser Name erscheint in der Quellenliste.`}
                </span>
              </div>

              <div className="field">
                <label htmlFor="csaf-wizard-kind"><Trans>Art der Quelle</Trans></label>
                <select
                  id="csaf-wizard-kind"
                  value={kind}
                  aria-describedby="csaf-wizard-kind-hint"
                  onChange={(event) => changeKind(event.target.value as CsafSourceKind)}
                >
                  {kinds.map((entry) => (
                    <option key={entry.kind} value={entry.kind}>
                      {entry.label}
                    </option>
                  ))}
                </select>
                <span id="csaf-wizard-kind-hint" className="hint">
                  {kindHint}
                </span>
              </div>

              <div className="field csaf-wizard-wide">
                <label htmlFor="csaf-wizard-url">
                  {kind === 'euvd' ? t`API-Basis (optional)` : t`Feed-URL`}
                </label>
                <input
                  id="csaf-wizard-url"
                  type="url"
                  inputMode="url"
                  value={kind === 'manual' ? '' : url}
                  placeholder={kind === 'euvd' ? t`Offizielle EUVD-API verwenden` : 'https://…'}
                  autoComplete="off"
                  spellCheck={false}
                  disabled={kind === 'manual'}
                  required={kind !== 'manual' && kind !== 'euvd'}
                  aria-invalid={!urlValid}
                  aria-describedby="csaf-wizard-url-hint"
                  onChange={(event) => changeUrl(event.target.value)}
                />
                <span id="csaf-wizard-url-hint" className={!urlValid ? 'err' : 'hint'}>
                  {kind === 'manual'
                    ? t`Für Upload-Quellen gibt es keine externe Verbindung.`
                    : urlValid
                      ? kindHint
                      : t`Bitte eine vollständige HTTP(S)-URL ohne Zugangsdaten eingeben.`}
                </span>
              </div>
            </div>
          </section>
        ) : null}

        {step === 2 ? (
          <section className="csaf-wizard-panel" aria-labelledby="csaf-wizard-test-title">
            <div className="csaf-wizard-panel-head">
              <div>
                <h3 id="csaf-wizard-test-title" ref={stepHeading} tabIndex={-1}>
                  <Trans>Anbindung prüfen</Trans>
                </h3>
                <p><Trans>Der Test lädt eine kleine Stichprobe und prüft das Format; er speichert nichts.</Trans></p>
              </div>
              <Chip
                tone={
                  kind === 'manual'
                    ? 'neutral'
                    : currentTestSucceeded && testResult?.status === 'ok'
                      ? 'good'
                      : 'warn'
                }
              >
                {kind === 'manual'
                  ? t`kein Netztest`
                  : currentTestSucceeded && testResult?.status === 'warning'
                    ? t`mit Warnungen geprüft`
                    : currentTestSucceeded
                      ? t`erfolgreich geprüft`
                      : t`Test ausstehend`}
              </Chip>
            </div>

            <dl className="csaf-wizard-review">
              <div>
                <dt><Trans>Quelle</Trans></dt>
                <dd>{cleanName}</dd>
              </div>
              <div>
                <dt><Trans>Art</Trans></dt>
                <dd>{kinds.find((entry) => entry.kind === kind)?.label ?? kind}</dd>
              </div>
              <div>
                <dt><Trans>Adresse</Trans></dt>
                <dd className="mono">{kind === 'manual' ? t`nur Datei-Uploads` : cleanUrl || t`offizielle EUVD-API`}</dd>
              </div>
            </dl>

            {kind === 'manual' ? (
              <Banner kind="ok" sub={t`Laden Sie CSAF-Dokumente nach dem Anlegen über „Datei hochladen“ in den Einstellungen der Quelle hoch.`}>
                <Trans>Upload-Quellen brauchen keinen Verbindungstest.</Trans>
              </Banner>
            ) : null}

            {testError !== null ? (
              <Banner kind="error" sub={t`Prüfen Sie die URL und wiederholen Sie den Test.`}>
                {testError}
              </Banner>
            ) : null}

            {testResult !== null && testedFingerprint === currentFingerprint ? (
              <Banner
                kind={currentTestSucceeded && testResult.status === 'ok' ? 'ok' : 'warn'}
                items={testResult.warnings.length > 0 ? testResult.warnings : undefined}
                sub={
                  <span className="csaf-wizard-testfacts">
                    <span>
                      <Icon name={testResult.reachable ? 'check' : 'close'} size={12} />{' '}
                      {testResult.reachable ? t`erreichbar` : t`nicht erreichbar`}
                    </span>
                    <span>
                      <Icon name={testResult.compatible ? 'check' : 'close'} size={12} />{' '}
                      {testResult.compatible ? t`kompatibles Format` : t`Format nicht kompatibel`}
                    </span>
                    {testResult.sample?.cves != null ? (
                      <span><Trans>{sampleCves} CVEs in der Stichprobe</Trans></span>
                    ) : null}
                    {testResult.sample ? (
                      <span>
                        <Trans>Beispiel: <code>{sampleId}</code>{sampleVersion}</Trans>
                      </span>
                    ) : null}
                  </span>
                }
              >
                {testResult.message}
              </Banner>
            ) : null}

            {kind !== 'manual' && testResult === null && testError === null ? (
              <EmptyState compact title={t`Noch nicht getestet`}>
                <Trans>Starten Sie „Verbindung testen“. Erst ein kompatibles Ergebnis gibt den nächsten Schritt frei.</Trans>
              </EmptyState>
            ) : null}
          </section>
        ) : null}

        {step === 3 ? (
          <section className="csaf-wizard-panel" aria-labelledby="csaf-wizard-rules-title">
            <div className="csaf-wizard-panel-head">
              <div>
                <h3 id="csaf-wizard-rules-title" ref={stepHeading} tabIndex={-1}>
                  <Trans>Einrichtung abschließen</Trans>
                </h3>
              </div>
            </div>

            <div className="csaf-wizard-final">
              <h4><Trans>Bereit zum Anlegen</Trans></h4>
              <p>
                {kind === 'manual' ? (
                  <Trans><strong>{cleanName}</strong> wird als Upload-Quelle mit einem Eintrag pro CVE angelegt.</Trans>
                ) : (
                  <Trans><strong>{cleanName}</strong> wird als aktive Remote-Quelle mit einem Eintrag pro CVE angelegt.</Trans>
                )}{' '}
                <Trans>Importiert werden alle CVEs dieser Quelle, unabhängig von der Portfolio-Relevanz.</Trans>
              </p>
            </div>

            {createError !== null ? (
              <Banner kind="error" sub={t`Die Eingaben und das Testergebnis bleiben erhalten.`}>
                {createError}
              </Banner>
            ) : null}
          </section>
        ) : null}
      </div>
    </Modal>
  );
}

export default CsafSourceWizard;
